/**
 * Hook genérico para utilitários de provider
 * 
 * Substitui o hook useZApiUtils específico do Z-API.
 * Internamente usa a edge function provider-proxy que
 * detecta automaticamente o provider da inbox.
 * 
 * Funcionalidades:
 * - Verificar se telefone tem WhatsApp
 * - Enviar indicador de digitação
 * - Marcar mensagem como lida
 * - Enviar localização (via send-message)
 * - Enviar lista de botões (via send-message)
 * - Enviar lista de opções (via send-message)
 * - Importar contatos do WhatsApp
 * - Buscar metadados de contato
 * - Buscar foto de perfil
 * - Obter QR Code para conexão
 * - Verificar status de conexão
 */

import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger para utilitários de provider (só aparece em desenvolvimento)
const providerLogger = createLogger('ProviderUtils');

// Interface para resultado de verificação de telefone
interface CheckPhoneResult {
  exists: boolean;
  jid?: string;
}

// Opções para indicador de presença
interface TypingOptions {
  type: 'composing' | 'recording' | 'paused';
  duration?: number; // em milissegundos
}

// Interface para contato do WhatsApp (retorno da importação)
export interface WhatsAppContact {
  phone: string;
  name: string | null;
  isMyContact: boolean;
  isBusiness: boolean;
  profilePictureUrl?: string;
}

// Interface para metadados do contato
export interface ContactMetadata {
  status?: string;
  description?: string;
  isBusiness?: boolean;
  businessName?: string;
  [key: string]: unknown;
}

// Interface para resultado de QR Code
export interface QRCodeResult {
  qrcode: string | null;  // Data URL da imagem base64
  connected: boolean;     // Se já está conectado
  error?: string;
  status?: 'waiting_qr' | 'scanning' | 'connected';  // Estado atual do QR Code
  message?: string;  // Mensagem para exibir no frontend
  statusProtected?: boolean;  // Indica que a inbox está inativa (bloqueio de proteção)
}

// Interface para status de conexão (NORMALIZADA - agnóstica de provider)
// O campo smartphoneConnected foi REMOVIDO intencionalmente:
// - Era um detalhe de implementação específico do Z-API
// - A lógica de verificação fica encapsulada no backend (provider-proxy)
// - Frontend deve usar APENAS o campo 'status' normalizado
export interface ConnectionStatusResult {
  connected: boolean;
  status: 'active' | 'inactive' | 'connecting' | 'error' | 'disconnected';
  webhooksConfigured?: boolean;
}

// Interface para contato a adicionar na agenda do WhatsApp
export interface ContactToAdd {
  firstName: string;
  lastName?: string;
  phone: string;
  webhooksConfigured?: boolean;
}

export function useProviderUtils() {
  /**
   * Verifica se um número de telefone tem WhatsApp
   */
  const checkPhoneExists = useCallback(async (
    inboxId: string,
    phone: string
  ): Promise<CheckPhoneResult> => {
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'check_phone',
        phone,
      },
    });

    if (error) {
      providerLogger.error('Erro ao verificar telefone', error);
      throw error;
    }

    return {
      exists: data?.data?.exists ?? false,
      jid: data?.data?.jid,
    };
  }, []);

  /**
   * Envia indicador de digitação (composing, recording, paused)
   */
  const sendTypingIndicator = useCallback(async (
    inboxId: string,
    phone: string,
    options: TypingOptions = { type: 'composing' }
  ): Promise<void> => {
    const { error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'send_presence',
        phone,
        presence_type: options.type,
        presence_duration: options.duration || 3000,
      },
    });

    if (error) {
      providerLogger.debug('Erro ao enviar indicador de digitação', error);
      // Não lança exceção - indicador não é crítico
    }
  }, []);

  /**
   * Marca uma mensagem como lida
   */
  const markMessageAsRead = useCallback(async (
    inboxId: string,
    phone: string,
    messageId: string
  ): Promise<void> => {
    const { error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'read_message',
        phone,
        message_id: messageId,
      },
    });

    if (error) {
      providerLogger.debug('Erro ao marcar como lida', error);
      // Não lança exceção - não é crítico
    }
  }, []);

  /**
   * Importa lista de contatos do WhatsApp
   * Retorna array de contatos normalizados
   */
  const importContacts = useCallback(async (
    inboxId: string
  ): Promise<WhatsAppContact[]> => {
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'import_contacts',
      },
    });

    if (error) {
      providerLogger.error('Erro ao importar contatos', error);
      throw error;
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Erro ao importar contatos');
    }

    return (data?.data as WhatsAppContact[]) || [];
  }, []);

  /**
   * Busca metadados de um contato específico
   */
  const getContactMetadata = useCallback(async (
    inboxId: string,
    phone: string
  ): Promise<ContactMetadata | null> => {
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'get_contact_metadata',
        phone,
      },
    });

    if (error) {
      providerLogger.debug('Erro ao buscar metadados', error);
      return null;
    }

    return data?.data as ContactMetadata || null;
  }, []);

  /**
   * Busca foto de perfil de um contato
   * Retorna URL da foto ou null se não encontrada
   */
  const getProfilePicture = useCallback(async (
    inboxId: string,
    phone: string
  ): Promise<string | null> => {
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'get_profile_picture',
        phone,
      },
    });

    if (error) {
      providerLogger.debug('Erro ao buscar foto de perfil', error);
      return null;
    }

    return data?.data?.url || null;
  }, []);

  /**
   * Faz download do avatar e salva no Storage
   * Retorna o caminho relativo do storage ou null se falhar
   * 
   * @param inboxId - ID da inbox para autenticação com o provider
   * @param phone - Telefone do contato
   * @param contactId - ID do contato (para salvar no caminho correto)
   */
  const downloadAvatar = useCallback(async (
    inboxId: string,
    phone: string,
    contactId: string
  ): Promise<string | null> => {
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'download_avatar',
        phone,
        contact_id: contactId,
      },
    });

    if (error) {
      providerLogger.error('Erro ao baixar avatar', error);
      return null;
    }

    if (!data?.success) {
      providerLogger.debug('Avatar não disponível', { error: data?.error });
      return null;
    }

    return data?.data?.storage_path || null;
  }, []);

  /**
   * Envia mensagem de localização
   */
  const sendLocation = useCallback(async (
    inboxId: string,
    phone: string,
    location: {
      latitude: number;
      longitude: number;
      name?: string;
      address?: string;
    }
  ): Promise<{ success: boolean; external_id?: string }> => {
    const { data, error } = await supabase.functions.invoke('send-message', {
      body: {
        inbox_id: inboxId,
        phone,
        message_type: 'location',
        latitude: location.latitude,
        longitude: location.longitude,
        location_name: location.name,
        location_address: location.address,
      },
    });

    if (error) {
      providerLogger.error('Erro ao enviar localização', error);
      throw error;
    }

    return {
      success: data?.success ?? false,
      external_id: data?.external_id,
    };
  }, []);

  /**
   * Envia lista de botões
   */
  const sendButtonList = useCallback(async (
    inboxId: string,
    phone: string,
    options: {
      message: string;
      title: string;
      buttons: Array<{ id: string; label: string }>;
    }
  ): Promise<{ success: boolean; external_id?: string }> => {
    const { data, error } = await supabase.functions.invoke('send-message', {
      body: {
        inbox_id: inboxId,
        phone,
        message_type: 'button_list',
        content: options.message,
        title: options.title,
        buttons: options.buttons,
      },
    });

    if (error) {
      providerLogger.error('Erro ao enviar lista de botões', error);
      throw error;
    }

    return {
      success: data?.success ?? false,
      external_id: data?.external_id,
    };
  }, []);

  /**
   * Envia lista de opções
   */
  const sendOptionList = useCallback(async (
    inboxId: string,
    phone: string,
    options: {
      title: string;
      description?: string;
      footer?: string;
      options: Array<{ id: string; title: string; description?: string }>;
    }
  ): Promise<{ success: boolean; external_id?: string }> => {
    const { data, error } = await supabase.functions.invoke('send-message', {
      body: {
        inbox_id: inboxId,
        phone,
        message_type: 'option_list',
        title: options.title,
        description: options.description,
        footer: options.footer,
        options: options.options,
      },
    });

    if (error) {
      providerLogger.error('Erro ao enviar lista de opções', error);
      throw error;
    }

    return {
      success: data?.success ?? false,
      external_id: data?.external_id,
    };
  }, []);

  /**
   * Envia ou remove reação em uma mensagem
   * Para remover reação, passe emoji como string vazia ''
   * 
   * @param inboxId - ID da inbox
   * @param phone - Telefone do contato
   * @param messageExternalId - ID externo da mensagem (external_id / shortId)
   * @param emoji - Emoji da reação (❤️, 👍, 😂, 😮, 😢, 🙏) ou '' para remover
   * @param direction - Direção da mensagem (inbound/outbound) para reconstruir messageId Z-API
   */
  /**
   * Envia reação a uma mensagem
   * 
   * @param inboxId - ID da inbox
   * @param phone - Telefone do contato (pode ser vazio para Instagram)
   * @param messageExternalId - ID externo da mensagem (external_id / shortId)
   * @param emoji - Emoji da reação (❤️, 👍, 😂, 😮, 😢, 🙏) ou '' para remover
   * @param direction - Direção da mensagem (inbound/outbound) para reconstruir messageId Z-API
   * @param contactId - ID do contato (necessário para Instagram que não usa phone)
   */
  const sendReaction = useCallback(async (
    inboxId: string,
    phone: string,
    messageExternalId: string,
    emoji: string,
    direction: 'inbound' | 'outbound',
    contactId?: string
  ): Promise<{ success: boolean }> => {
    const { data, error } = await supabase.functions.invoke('send-message', {
      body: {
        inbox_id: inboxId,
        phone,
        contact_id: contactId, // Necessário para Instagram que usa channel_ids.instagram_igsid
        message_type: 'reaction',
        reaction_emoji: emoji,
        reaction_message_id: messageExternalId,
        reaction_direction: direction, // Necessário para reconstruir messageId no formato Z-API
      },
    });

    if (error) {
      providerLogger.error('Erro ao enviar reação', error);
      throw error;
    }

    return { success: data?.success ?? false };
  }, []);

  /**
   * Deleta uma mensagem no WhatsApp
   * 
   * IMPORTANTE: WhatsApp só permite deletar "para todos" em ~1h após envio.
   * Após esse período, a mensagem só será deletada localmente.
   * 
   * @param inboxId - ID da inbox
   * @param phone - Telefone do contato
   * @param messageId - ID externo da mensagem (external_id)
   * @param deleteForEveryone - Se true, tenta deletar para todos (padrão)
   */
  const deleteMessage = useCallback(async (
    inboxId: string,
    phone: string,
    messageId: string,
    deleteForEveryone: boolean = true
  ): Promise<{ success: boolean; deletedOnWhatsApp: boolean; error?: string }> => {
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'delete_message',
        phone,
        message_id: messageId,
        delete_for_everyone: deleteForEveryone,
      },
    });

    if (error) {
      providerLogger.error('Erro ao deletar mensagem', error);
      return { success: false, deletedOnWhatsApp: false, error: error.message };
    }

    return {
      success: data?.success ?? false,
      deletedOnWhatsApp: data?.data?.deletedOnWhatsApp ?? false,
      error: data?.error,
    };
  }, []);

  /**
   * Obtém QR Code para conexão WhatsApp
   * 
   * O QR Code expira a cada ~20 segundos.
   * Recomendação: refresh entre 10-20s, máx 3 tentativas automáticas.
   * 
   * @param inboxId - ID da inbox para obter QR Code
   * @returns QRCodeResult com qrcode (data URL) ou connected=true se já conectado
   */
  const getQRCode = useCallback(async (
    inboxId: string
  ): Promise<QRCodeResult> => {
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'get_qrcode',
      },
    });

    if (error) {
      providerLogger.error('Erro ao obter QR Code', error);
      return {
        qrcode: null,
        connected: false,
        error: error.message,
      };
    }

    if (!data?.success) {
      return {
        qrcode: null,
        connected: false,
        error: data?.error || 'Erro ao obter QR Code',
      };
    }

    return {
      qrcode: data.data?.qrcode || null,
      connected: data.data?.connected ?? false,
      status: data.data?.status,  // Passa status para frontend (waiting_qr, scanning, connected)
      message: data.data?.message,  // Mensagem para exibir
      statusProtected: data.data?.statusProtected ?? false,  // Indica inbox inativa
    };
  }, []);

  /**
   * Verifica status da conexão WhatsApp
   * 
   * Retorna informações sobre conexão e se webhooks foram configurados.
   * Atualiza automaticamente o status da inbox no banco.
   * 
   * @param inboxId - ID da inbox para verificar status
   * @returns ConnectionStatusResult com status atual
   */
  const getConnectionStatus = useCallback(async (
    inboxId: string
  ): Promise<ConnectionStatusResult> => {
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'get_connection_status',
      },
    });

    if (error) {
      console.error('[useProviderUtils] Erro ao verificar status:', error);
      return {
        connected: false,
        status: 'error',
      };
    }

    if (!data?.success) {
      return {
        connected: false,
        status: 'error',
      };
    }

    // Retorna apenas campos normalizados - smartphoneConnected foi removido
    // A lógica específica de cada provider está encapsulada no backend
    return {
      connected: data.data?.connected ?? false,
      status: data.data?.status || 'disconnected',
      webhooksConfigured: data.data?.webhooksConfigured,
    };
  }, []);

  /**
   * Adiciona contatos à agenda do celular conectado ao WhatsApp
   * 
   * IMPORTANTE: Só funciona se a conta recebeu a atualização e tem permissão
   * de adicionar contatos nas configurações do WhatsApp.
   * 
   * @param inboxId - ID da inbox conectada
   * @param contacts - Array de contatos para adicionar
   */
  const addToPhoneContacts = useCallback(async (
    inboxId: string,
    contacts: ContactToAdd[]
  ): Promise<{ success: boolean; error?: string }> => {
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'add_to_phone_contacts',
        contacts_to_add: contacts,
      },
    });

    if (error) {
      console.error('[useProviderUtils] Erro ao adicionar contatos à agenda:', error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Erro ao adicionar contatos' };
    }

    return { success: true };
  }, []);

  /**
   * Desconecta a sessão WhatsApp do provider
   * 
   * IMPORTANTE: Isso apenas desconecta a sessão do WhatsApp.
   * A inbox permanece ativa no sistema e pode ser reconectada via QR Code.
   * 
   * @param inboxId - ID da inbox para desconectar
   */
  const disconnect = useCallback(async (
    inboxId: string
  ): Promise<{ success: boolean; error?: string }> => {
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'disconnect',
      },
    });

    if (error) {
      console.error('[useProviderUtils] Erro ao desconectar:', error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Erro ao desconectar' };
    }

    return { success: true };
  }, []);

  /**
   * Cria sessão no provider (específico WAHA)
   * 
   * Alguns providers como WAHA requerem criação explícita de sessão
   * antes de gerar QR Code. O frontend permanece agnóstico - a 
   * edge function provider-proxy roteia automaticamente.
   * 
   * Para providers que não suportam esta action, retorna erro.
   * 
   * @param inboxId - ID da inbox para criar sessão
   * @returns Resultado com sessionName se criação bem sucedida
   */
  const createSession = useCallback(async (
    inboxId: string
  ): Promise<{ success: boolean; sessionName?: string; error?: string }> => {
    providerLogger.info('Criando sessão para inbox', { inboxId });
    
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'create_session',
      },
    });

    if (error) {
      providerLogger.error('Erro ao criar sessão', error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Erro ao criar sessão' };
    }

    providerLogger.info('Sessão criada com sucesso', { sessionName: data.data?.sessionName });
    return { 
      success: true, 
      sessionName: data.data?.sessionName 
    };
  }, []);

  /**
   * Reinicia sessão no provider (específico WAHA)
   * 
   * Usado quando a sessão entra em status FAILED ou STOPPED.
   * A edge function provider-proxy roteia automaticamente.
   * 
   * Para providers que não suportam esta action, retorna erro.
   * 
   * @param inboxId - ID da inbox para reiniciar sessão
   * @returns Resultado da operação
   */
  const restartSession = useCallback(async (
    inboxId: string
  ): Promise<{ success: boolean; error?: string }> => {
    providerLogger.info('Reiniciando sessão para inbox', { inboxId });
    
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'restart_session',
      },
    });

    if (error) {
      providerLogger.error('Erro ao reiniciar sessão', error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Erro ao reiniciar sessão' };
    }

    providerLogger.info('Sessão reiniciada com sucesso');
    return { success: true };
  }, []);

  /**
   * Solicita código de pareamento para autenticação alternativa ao QR Code
   * 
   * MULTI-PROVIDER: Funciona com qualquer provider que suporte pairing code.
   * O roteamento é feito automaticamente no backend baseado no provider da inbox.
   * 
   * IMPORTANTE: O pairing code é uma alternativa ao QR Code, não um substituto.
   * Nem todos os providers suportam esta funcionalidade.
   * 
   * Providers suportados:
   * - WAHA: ✅ Implementado
   * - Evolution: ⏳ Placeholder (não suportado ainda)
   * - Z-API: ❌ Não suportado
   * 
   * @param inboxId - ID da inbox para solicitar código
   * @param phoneNumber - Número de telefone no formato DDI + DDD + número (ex: 5511999998888)
   * @returns Código de pareamento no formato XXXX-XXXX ou erro
   */
  const requestPairingCode = useCallback(async (
    inboxId: string,
    phoneNumber: string
  ): Promise<{ success: boolean; code?: string; message?: string; error?: string }> => {
    providerLogger.info('Solicitando pairing code', { inboxId, phone: phoneNumber.substring(0, 4) + '****' });
    
    const { data, error } = await supabase.functions.invoke('provider-proxy', {
      body: {
        inbox_id: inboxId,
        action: 'request_pairing_code',
        phone_number: phoneNumber,
      },
    });

    if (error) {
      providerLogger.error('Erro ao solicitar pairing code', error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Erro ao solicitar código de pareamento' };
    }

    providerLogger.info('Pairing code obtido com sucesso');
    return { 
      success: true, 
      code: data.data?.code,
      message: data.data?.message,
    };
  }, []);

  /**
   * Inscrever para receber eventos de presença (digitando/gravando) de um contato
   * 
   * IMPORTANTE: Chamada obrigatória para Z-API e WAHA ativarem o envio de
   * webhooks de presença (PresenceChatCallback / presence.update).
   * Sem esta chamada, os webhooks de presença estão configurados mas não são acionados.
   * 
   * @param inboxId - ID da inbox para autenticação
   * @param phone - Telefone do contato para inscrever
   * @returns void - Erros são silenciados (funcionalidade não-crítica)
   */
  const subscribePresence = useCallback(async (
    inboxId: string,
    phone: string
  ): Promise<void> => {
    try {
      providerLogger.debug('Inscrevendo para eventos de presença', { 
        inboxId: inboxId.substring(0, 8) + '...', 
        phone: phone.substring(0, 6) + '...' 
      });
      
      const { error } = await supabase.functions.invoke('provider-proxy', {
        body: {
          inbox_id: inboxId,
          action: 'subscribe_presence',
          phone,
        },
      });

      if (error) {
        providerLogger.warn('Erro ao inscrever presença (não crítico)', error);
        // Não lançar erro - funcionalidade opcional
      }
    } catch (err) {
      providerLogger.warn('Falha ao inscrever presença', err);
      // Silenciar erro - presença é funcionalidade não-crítica
    }
  }, []);

  return {
    checkPhoneExists,
    sendTypingIndicator,
    markMessageAsRead,
    importContacts,
    getContactMetadata,
    getProfilePicture,
    downloadAvatar,
    sendLocation,
    sendButtonList,
    sendOptionList,
    sendReaction,
    deleteMessage,
    getQRCode,
    getConnectionStatus,
    addToPhoneContacts,
    disconnect,
    createSession,
    restartSession,
    requestPairingCode,
    subscribePresence, // Inscrição para eventos de presença (typing/recording)
  };
}